import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { Lock } from "@mui/icons-material"
import { MuiButton, MuiCard, InputType } from "@ace/ace-ui-web"
import { CardContent, Typography } from "@mui/material"
import { useNavigate, useSearchParams, Navigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { resetPassword, fetchResetTokenInfo } from "../../redux/PasswordResetReducer/passwordResetSlice"
import FormField from "../../components/formfield"

// Styling
import styles from "./resetPassword.module.css"

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const [searchParams] = useSearchParams()
    const token = searchParams.get("token")
    const { resetLoading, tokenInfo, tokenInfoLoading } = useAppSelector((state) => state.passwordReset)

    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [errors, setErrors] = useState<Record<string, string>>({})

    useEffect(() => {
        if (token) {
            dispatch(fetchResetTokenInfo(token))
        }
    }, [dispatch, token])

    // Guard: no token in URL means this page was not accessed via a reset link
    if (!token) {
        return <Navigate to="/login" replace />
    }

    const validatePassword = (pwd: string): string | null => {
        if (pwd.length < 8) {
            return "Password must be at least 8 characters"
        }

        if (tokenInfo) {
            const pwdLower = pwd.toLowerCase()
            const nameParts = [tokenInfo.firstName.toLowerCase(), tokenInfo.lastName.toLowerCase()]
            for (const part of nameParts) {
                if (part && pwdLower.includes(part)) {
                    return "Password cannot contain your name"
                }
            }
        }

        const weakPasswords = ["password123", "ace12345", "password", "12345678"]
        if (weakPasswords.includes(pwd.toLowerCase())) {
            return "This password is too weak or commonly used"
        }

        const hasUppercase = /[A-Z]/.test(pwd)
        const hasLowercase = /[a-z]/.test(pwd)
        const hasDigit = /\d/.test(pwd);
        const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)

        const categoriesCount = [hasUppercase, hasLowercase, hasDigit, hasSpecial]
            .filter(Boolean).length

        if (categoriesCount < 3) {
            return "Password must contain at least 3 of: uppercase, lowercase, numbers, special characters"
        }

        return null
    }

    const handleReset = async () => {
        const newErrors: Record<string, string> = {}

        if (newPassword.trim()) {
            const passwordError = validatePassword(newPassword)
            if (passwordError) newErrors.newPassword = passwordError
        } else {
            newErrors.newPassword = "Password is required"
        }

        if (confirmPassword.trim()) {
            if (newPassword !== confirmPassword) {
                newErrors.confirmPassword = "Passwords do not match"
            }
        } else {
            newErrors.confirmPassword = "Please re-enter your password"
        }

        setErrors(newErrors)

        if (Object.keys(newErrors).length > 0) {
            toast.error("Please fix the errors before submitting")
            return
        }

        try {
            const result = await dispatch(resetPassword({ token, newPassword })).unwrap()
            toast.success(result?.message || "Password reset successfully! Please sign in with your new password.")
        } catch (error: any) {
            toast.error(error || "Failed to reset password. Please try again.")
        } finally {
            setTimeout(() => navigate("/login"), 2500)
        }
    }

    return (
        <div className={styles.appContainer}>
            <div className={styles.contentWrapper}>
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <MuiCard
                        borderRadius={2}
                        elevation={5}
                        height="auto"
                        width="auto"
                        padding={1}
                        variant="elevation"
                    >
                        <div className={styles.cardTitle}>
                            <Typography variant="h6" component="h2" className={styles.cardTitleText}>
                                Reset Your Password
                            </Typography>
                            <Typography variant="body2" className={styles.cardSubtitleText}>
                                Enter and confirm your new password below
                            </Typography>
                        </div>
                        <CardContent>
                            <form className={styles.form}>
                                {/* New Password */}
                                <FormField
                                    label="New Password"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value)
                                        if (errors.newPassword) setErrors(prev => ({ ...prev, newPassword: "" }))
                                    }}
                                    type={InputType.Password}
                                    placeholder="Enter your new password"
                                    muiIcon={Lock}
                                    helperText="Must be at least 8 characters"
                                    error={errors.newPassword}
                                    disabled={tokenInfoLoading}
                                />

                                {/* Confirm New Password */}
                                <FormField
                                    label="Confirm New Password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value)
                                        if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: "" }))
                                    }}
                                    type={InputType.Password}
                                    placeholder="Re-enter your new password"
                                    muiIcon={Lock}
                                    error={errors.confirmPassword}
                                    disabled={tokenInfoLoading}
                                />

                                <div className={styles.buttonGroup}>
                                    <motion.div
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        className={styles.buttonFull}
                                    >
                                        <MuiButton
                                            size="large"
                                            disabled={resetLoading || tokenInfoLoading}
                                            onClick={handleReset}
                                            sx={{ width: "100%" }}
                                        >
                                            {resetLoading ? (
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                    className={styles.spinner}
                                                />
                                            ) : (
                                                "Reset Password"
                                            )}
                                        </MuiButton>
                                    </motion.div>
                                </div>
                            </form>
                        </CardContent>
                    </MuiCard>
                </motion.div>
            </div>
        </div>
    )
}

export default ResetPasswordPage